import { 
  formatDateForPrint, 
  escapeHtml, 
  createPrintIframe,
  getCompanyInfo,
  getMetalType,
  getMetalLabel
} from './printUtils';

/**
 * Format nombre avec précision 2 décimales - masque les zéros
 */
const formatNumber2Dec = (num) => {
  if (!num && num !== 0) return '';
  const formatted = parseFloat(num).toFixed(2);
  // Retourne une chaîne vide si la valeur est 0.00
  return formatted === '0.00' ? '' : formatted;
};

/**
 * Styles CSS pour le livre de police
 */
const getPoliceBookStyles = () => `
  @page {
    margin: 20mm 15mm;
    size: A4 landscape;
  }
  
  body {
    font-family: 'Segoe UI', 'Arial', sans-serif;
    margin: 0;
    padding: 0;
    color: #000;
    font-size: 11px;
    line-height: 1.4;
    background: white;
  }
  
  .header {
    text-align: center;
    padding: 40px 20px;
    margin: 0 auto;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 60vh;
  }
  
  .company-info {
    font-weight: 700;
    font-size: 24px;
    margin-bottom: 15px;
    color: #000;
  }
  
  .company-details {
    font-size: 18px;
    margin: 8px 0;
    color: #000;
    font-weight: 500;
  }
  
  .document-title {
    font-size: 28px;
    font-weight: 800;
    text-transform: uppercase;
    margin: 30px 0 15px 0;
    letter-spacing: 2px;
    color: #000;
  }
  
  .period-info {
    font-size: 20px;
    margin: 20px 0;
    font-weight: 600;
    display: inline-block;
    padding: 15px 30px;
    border: 2px solid #000;
    border-radius: 25px;
    color: #000;
  }
  
  .metal-section {
    page-break-before: always;
    page-break-inside: avoid;
    margin-bottom: 30px;
    margin-top: 20px;
  }
  
  .new-year-break {
    page-break-before: always;
  }
  
  .gold-section {
    margin-top: 20px;
  }
  
  .metal-table {
    border-collapse: collapse;
    width: 100%;
    font-size: 10px;
    border: 3px solid #000;
    border-radius: 15px;
    margin: 20px auto 30px auto;
    table-layout: fixed;
    background: white;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
  
  .gold-table {
    table-layout: fixed;
    width: 100% !important;
  }
  
  th, td {
    border: 1px solid #000;
    padding: 6px 4px;
    text-align: center;
    word-wrap: break-word;
  }
  
  /* Suppression des bordures doubles */
  th {
    border-top: none;
    border-left: none;
  }
  
  td {
    border-top: none;
    border-left: none;
  }
  
  /* Première colonne */
  th:first-child, td:first-child {
    border-left: none;
  }
  
  /* Première ligne d'en-têtes */
  thead tr:first-child th {
    border-top: none;
  }
  
  th {
    color: #000;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 9px;
    padding: 8px 4px;
    letter-spacing: 0.5px;
  }
  
  /* Force bordure épaisse pour toutes les tables */
  .metal-table {
    border: 3px solid #000 !important;
  }
  
  .metal-table th, .metal-table td {
    border: 1px solid #000 !important;
  }
  
  .metal-table th {
    border-top: none !important;
    border-left: none !important;
  }
  
  .metal-table td {
    border-top: none !important;
    border-left: none !important;
  }
  
  .metal-table th:first-child, .metal-table td:first-child {
    border-left: none !important;
  }
  
  .metal-table thead tr:first-child th {
    border-top: none !important;
  }
  
  /* Style pour la première ligne (MÉTAL :) */
  .metal-title-row th {
    background: #f1f3f4 !important;
    font-size: 12px !important;
    font-weight: 800 !important;
    padding: 12px !important;
    border-bottom: 3px solid #000 !important;
    border-top-left-radius: 15px !important;
    border-top-right-radius: 15px !important;
    text-align: center !important;
    border-top: none !important;
    border-left: none !important;
    border-right: 1px solid #000 !important;
  }
  
  /* Coins arrondis pour la première ligne - spécifique */
  .metal-title-row th:first-child {
    border-top-left-radius: 15px !important;
    border-left: none !important;
  }
  
  .metal-title-row th:last-child {
    border-top-right-radius: 15px !important;
    border-right: none !important;
  }
  
  /* Coins arrondis pour la première ligne */
  thead tr:first-child th:first-child {
    border-top-left-radius: 15px;
  }
  
  thead tr:first-child th:last-child {
    border-top-right-radius: 15px;
  }
  
  /* Coins arrondis pour la dernière ligne */
  tbody tr:last-child td:first-child {
    border-bottom-left-radius: 15px;
  }
  
  tbody tr:last-child td:last-child {
    border-bottom-right-radius: 15px;
  }
  
  tbody tr:nth-child(even) {
    background: white;
  }
  
  td.l, th.l { text-align: left; }
  td.r, th.r { text-align: right; }
  td.c, th.c { text-align: center; }
  
  .row-number {
    width: 25px;
    font-weight: 700;
    color: #000;
    border-right: 2px solid #000;
  }
  
  .row-number-gold {
    width: 25px;
    font-weight: 700;
    color: #000;
    border-right: 2px solid #000;
  }
  
  /* Colonnes pour métaux autres que l'or (6 colonnes) - optimisées pour largeur page */
  .date-col { width: 80px; }
  .source-col { width: 200px; }
  .designation-col { width: 150px; }
  .amount-col { width: 80px; }
  
  /* Colonnes adaptées pour l'or (7 colonnes) - utilisation optimale de l'espace */
  .date-col-gold { width: 8%; }
  .source-col-gold { width: 24%; }
  .designation-col-gold { width: 20%; }
  .amount-col-gold { width: 11%; }
  
  /* Force les bonnes largeurs pour l'or avec optimisation espace */
  .gold-table .row-number-gold { width: 3% !important; }
  .gold-table .date-col-gold { width: 8% !important; }
  .gold-table .source-col-gold { width: 24% !important; }
  .gold-table .designation-col-gold { width: 20% !important; }
  .gold-table .amount-col-gold { width: 11% !important; }
  
  tfoot td {
    font-weight: 700;
    border-top: 3px solid #000;
    color: #000;
    font-size: 13px;
  }
  
  .neg { 
    color: #dc2626; 
    font-weight: 700; 
  }
  .pos { 
    color: #16a34a; 
    font-weight: 700; 
  }
  
  .footer {
    margin-top: 30px;
    border-top: 3px solid #000;
    padding-top: 20px;
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: #000;
    background: #f8f9fa;
    padding: 25px;
    border-radius: 15px;
    border: 2px solid #000;
  }
  
  .signature-box {
    border: 3px solid #000;
    border-radius: 10px;
    width: 250px;
    height: 80px;
    text-align: center;
    padding-top: 12px;
    background: white;
    font-weight: 600;
  }
  
  .legal-mention {
    font-size: 11px;
    font-style: italic;
    text-align: center;
    margin-top: 20px;
    color: #000;
    background: #f1f3f4;
    padding: 15px;
    border-radius: 10px;
    border: 2px solid #000;
    font-weight: 500;
  }
  
  @media print {
    body { 
      margin: 0;
      padding: 0;
      background: white !important;
      -webkit-print-color-adjust: exact;
      color-adjust: exact;
    }
    .no-print { display: none !important; }
    
    .header {
      margin-bottom: 0;
      page-break-after: always;
      min-height: 80vh;
    }
    
    .new-year-break {
      page-break-before: always !important;
    }
    
    .metal-section { 
      page-break-before: always;
      page-break-inside: avoid;
      break-inside: avoid;
      margin: 20px 0;
      padding: 0;
    }
    
    .metal-section:first-child {
      page-break-before: auto;
    }
    
    .metal-table {
      page-break-inside: avoid;
      break-inside: avoid;
      margin: 20px auto 30px auto !important;
      border-collapse: collapse;
      border: 3px solid #000 !important;
      border-radius: 15px !important;
      overflow: hidden !important;
      box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
      width: 100% !important;
    }
    
    .metal-table thead {
      display: table-header-group;
    }
    
    .metal-table tbody {
      display: table-row-group;
    }
    
    .metal-table tbody tr {
      page-break-inside: avoid;
      break-inside: avoid;
    }
    
    /* Force les coins arrondis à l'impression */
    .metal-title-row th {
      background: #f1f3f4 !important;
      font-size: 12px !important;
      font-weight: 800 !important;
      padding: 12px !important;
      border-bottom: 3px solid #000 !important;
      text-align: center !important;
    }
    
    .metal-title-row th:first-child {
      border-top-left-radius: 15px !important;
    }
    
    .metal-title-row th:last-child {
      border-top-right-radius: 15px !important;
    }
    
    /* Force les bordures à l'impression - uniformité garantie */
    .metal-table {
      border: 3px solid #000 !important;
      border-radius: 15px !important;
      overflow: hidden !important;
    }
    
    .metal-table th, .metal-table td {
      border: 1px solid #000 !important;
      -webkit-print-color-adjust: exact;
    }
    
    /* Suppression des bordures doubles à l'impression */
    .metal-table th {
      border-top: none !important;
      border-left: none !important;
    }
    
    .metal-table td {
      border-top: none !important;
      border-left: none !important;
    }
    
    .metal-table th:first-child, .metal-table td:first-child {
      border-left: none !important;
    }
    
    /* Coins arrondis pour la dernière ligne du tableau */
    tbody tr:last-child td:first-child {
      border-bottom-left-radius: 15px !important;
    }
    
    tbody tr:last-child td:last-child {
      border-bottom-right-radius: 15px !important;
    }
    
    .metal-table thead tr:first-child th {
      border-top: none !important;
      background: #f8f9fa !important;
    }
  }
`;

/**
 * Génère une section pour un métal donné pour une année spécifique
 */
const generateMetalSectionForYear = (metalId, metalYearData, metauxList, tab, filters, fmtC, year) => {
  const metalObj = metauxList.find(m => String(m.id_metal) === String(metalId)) || {};
  const { isGold } = getMetalType(metalId);
  const metalLabel = getMetalLabel(metalObj, metalId);
  
  // Trier les données par ordre chronologique croissant (date + id)
  const rows = [...metalYearData].sort((a, b) => {
    if (a.date_mouvement === b.date_mouvement) {
      return (a.id || 0) - (b.id || 0);
    }
    return a.date_mouvement.localeCompare(b.date_mouvement);
  });
  
  const headerColsLabel = (tab === 0 ? 'Provenance/Destination' : (tab === 1 ? 'Fournisseur' : 'Client'));
  
  let rowCounter = 1;
  const bodyRowsHtml = rows.map(r => {
    const entreeDisplay = (tab === 0 && r.__source === 'FOUR') ? '' : formatNumber2Dec(r.entree_or_fin != null ? r.entree_or_fin : r.entree_fin);
    const sourceLabel = (tab === 0 ? 
      (r.__source === 'CLAL' ? 'CLAL - Stocks internes' : (r.fournisseur_label || r.client_label || r.fournisseur_raison_soc || r.fournisseur_id || r.client_id || r.__source)) : 
      (tab === 1 ? (r.fournisseur_label || r.fournisseur_raison_soc || r.fournisseur_id) : 
      (r.client_label || r.client || r.client_id)));
    
    // Pour l'or seulement, afficher la colonne "Sortie Or 750"
    const sortie750Cell = isGold ? `<td class="r">${formatNumber2Dec(r.sortie_or_750)}</td>` : '';
    const sortieDisplay = fmtC(r.__sortieFinCents);
    const sortieFormatted = (sortieDisplay === '0.00' || sortieDisplay === '0,00') ? '' : sortieDisplay;
    
    return `<tr>
      <td class="c">${rowCounter++}</td>
      <td class="c">${formatDateForPrint(r.date_mouvement)}</td>
      <td class="l">${escapeHtml(sourceLabel || '')}</td>
      <td class="l">${escapeHtml(r.designation || '')}</td>
      <td class="r">${entreeDisplay}</td>
      ${sortie750Cell}
      <td class="r">${sortieFormatted}</td>
    </tr>`;
  }).join('');
  
  // En-têtes de colonnes adaptés selon le métal
  const sortie750Header = isGold ? `<th class="amount-col-gold">Sortie<br>Or 750<br>(g)</th>` : '';
  const colCount = isGold ? 7 : 6;
  
  return `
    <div class="metal-section ${isGold ? 'gold-section' : ''}">
      <table class="metal-table ${isGold ? 'gold-table' : ''}">
        <thead>
          <tr class="metal-title-row">
            <th colspan="${colCount}">
              MÉTAL : ${escapeHtml(metalLabel.toUpperCase())}
            </th>
          </tr>
          <tr>
            <th class="${isGold ? 'row-number-gold' : 'row-number'}">#</th>
            <th class="${isGold ? 'date-col-gold' : 'date-col'}">Date</th>
            <th class="${isGold ? 'source-col-gold' : 'source-col'}">${headerColsLabel}</th>
            <th class="${isGold ? 'designation-col-gold' : 'designation-col'}">Désignation</th>
            <th class="${isGold ? 'amount-col-gold' : 'amount-col'}">Entrée<br>${isGold ? 'Or Fin' : 'Métal'}<br>(g)</th>
            ${sortie750Header}
            <th class="${isGold ? 'amount-col-gold' : 'amount-col'}">Sortie<br>${isGold ? 'Or Fin' : 'Métal'}<br>(g)</th>
          </tr>
        </thead>
        <tbody>
          ${bodyRowsHtml}
        </tbody>
      </table>
    </div>`;
};

/**
 * Impression complète du livre de police (tous métaux décomposés par année)
 */
export const handlePoliceBookPrint = (progressiveByMetal, metauxList, tab, filters, fmtC) => {
  const allMetalIds = Object.keys(progressiveByMetal.displayGroups).filter(metalId => 
    progressiveByMetal.displayGroups[metalId] && progressiveByMetal.displayGroups[metalId].length > 0
  );
  
  if (!allMetalIds.length) return;
  
  const companyInfo = getCompanyInfo();
  
  // Grouper les données par année
  const dataByYear = {};
  
  allMetalIds.forEach(metalId => {
    const metalData = progressiveByMetal.displayGroups[metalId];
    metalData.forEach(row => {
      if (row.date_mouvement) {
        const year = row.date_mouvement.split('-')[0];
        if (!dataByYear[year]) {
          dataByYear[year] = {};
        }
        if (!dataByYear[year][metalId]) {
          dataByYear[year][metalId] = [];
        }
        dataByYear[year][metalId].push(row);
      }
    });
  });
  
  const years = Object.keys(dataByYear).sort();
  
  // Générer le contenu pour chaque année
  let yearSections = '';
  
  years.forEach((year, yearIndex) => {
    const yearMetalIds = Object.keys(dataByYear[year]);
    
    // Page de garde pour chaque année (comme une impression séparée)
    const yearCoverPage = `
      <div class="header ${yearIndex > 0 ? 'new-year-break' : ''}">
        <div class="company-info">${escapeHtml(companyInfo.name)}</div>
        <div class="company-details">${escapeHtml(companyInfo.address)}</div>
        <div class="company-details">${escapeHtml(companyInfo.city)}</div>
        <div class="company-details">${escapeHtml(companyInfo.siret)}</div>
        
        <div class="document-title">Livre de Stock</div>
        <div class="document-title" style="font-size: 14px;">ANNÉE ${year}</div>
        <div class="period-info">Du 01/01/${year} au 31/12/${year}</div>
      </div>
    `;
    
    // Générer les sections de métaux pour cette année
    const yearMetalSections = yearMetalIds.map(metalId => 
      generateMetalSectionForYear(metalId, dataByYear[year][metalId], metauxList, tab, filters, fmtC, year)
    ).join('');
    
    yearSections += yearCoverPage + yearMetalSections;
  });
  
  const html = `<!DOCTYPE html><html><head><meta charset='utf-8'/><title>Livre de Stock - Tous Métaux par Année</title>
  <style>${getPoliceBookStyles()}</style></head><body>
  
  ${yearSections}
  
  <script>
    window.addEventListener('afterprint', () => {
      setTimeout(() => {
        if (parent && parent.document && parent.document.body) {
          parent.document.body.removeChild(frameElement);
        }
      }, 100);
    });
    setTimeout(() => window.print(), 100);
  </script>
  </body></html>`;
  
  createPrintIframe(html);
};